import {
  MASTER_COMPANY_INDUSTRY_SECTORS,
  MASTER_COMPANY_EMPLOYEE_BANDS,
  normaliseCompanyWebsite,
} from "@legal-500/reference-data";
import { Button, Modal } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { masterCompaniesService, type ImportResult } from "../../../api/masterCompanies.service";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { usePermissions } from "../../../hooks/usePermissions";
import styles from "./MasterCompanies.module.css";

/** Every field is a control value, so the two enum-backed selects hold "" while unset. */
type CompanyForm = { name: string; website: string; employees: string; industrySector: string };

const EMPTY_COMPANY: CompanyForm = { name: "", website: "", employees: "", industrySector: "" };
const TEMPLATE = "name,website,employees,industry_sector\nExample Company,example.com,,\n";
/** The submit button sits in the modal's single action row, outside the form it submits. */
const COMPANY_FORM_ID = "add-company-form";
// users-api holds an import response open for 110 seconds, so anything still waiting past this
// is hung rather than slow and the operator is better served by an error than by a spinner.
const REQUEST_TIMEOUT_MS = 120_000;
const TIMED_OUT = "timed out";

// A CSV validation failure can report one line per bad row, so each detail keeps its own line
// rather than running together into one sentence; .warning's white-space: pre-line renders it.
function describeError(error: unknown): string {
  const failure = error as { message?: string; details?: { message: string }[] };
  return (
    failure?.details?.map(detail => detail.message).join("\n") ||
    failure?.message ||
    "Request failed."
  );
}

type Operation = "Reading CSV" | "Validating CSV" | "Importing companies" | "Creating company";

function ProgressNotice({ operation }: { operation: Operation }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const startedAt = Date.now();
    const timer = window.setInterval(
      () => setElapsed(Math.floor((Date.now() - startedAt) / 1000)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, []);
  const validating = operation === "Reading CSV" || operation === "Validating CSV";
  return (
    <div className={`${styles.notice} ${styles.progress}`}>
      <span className={styles.spinner} aria-hidden="true" />
      <div>
        <p role="status" aria-live="polite">
          <strong>{operation}…</strong>
        </p>
        <p>
          {elapsed}s elapsed.{" "}
          {validating
            ? "No companies are saved during validation."
            : "Please keep this page open until the result appears."}
        </p>
        {elapsed >= 30 && (
          <p role="status">
            This is taking longer than expected.{" "}
            {validating
              ? "You can refresh safely and try validating again."
              : "If the connection is lost, retrying the same company data will skip duplicates."}
          </p>
        )}
      </div>
    </div>
  );
}

export function CompanyCreationModal({
  kind,
  onClose,
  onSaved,
}: {
  kind: "single" | "bulk";
  onClose: () => void;
  onSaved: () => void;
}) {
  const { hasPermission } = usePermissions();
  const [file, setFile] = useState<File | null>(null);
  const [validatedCsv, setValidatedCsv] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [operation, setOperation] = useState<Operation | null>(null);
  const busy = operation !== null;
  const canWrite = hasPermission("staff.website_companies.write");
  const [error, setError] = useState<string | null>(null);
  const [company, setCompany] = useState<CompanyForm>(EMPTY_COMPANY);
  const [message, setMessage] = useState<string | null>(null);
  const request = useRef<{ controller: AbortController; timer: number } | null>(null);

  function resetPreview() {
    setValidatedCsv(null);
    setResult(null);
    setError(null);
    setMessage(null);
  }

  /**
   * Each CSV call runs under its own controller so the timeout, and the operator closing the
   * modal, both end the wait. Aborting only stops this browser listening: a write the server
   * has already started still finishes, and re-importing the same file skips what landed.
   */
  function startRequest(): AbortSignal {
    const controller = new AbortController();
    request.current = {
      controller,
      timer: window.setTimeout(() => controller.abort(TIMED_OUT), REQUEST_TIMEOUT_MS),
    };
    return controller.signal;
  }

  function finishRequest() {
    if (request.current) window.clearTimeout(request.current.timer);
    request.current = null;
    setOperation(null);
  }

  async function validateFile() {
    if (!file) return;
    resetPreview();
    setOperation("Reading CSV");
    const signal = startRequest();
    try {
      if (file.size > 5 * 1024 * 1024) throw new Error("CSV must be no larger than 5 MB.");
      const csv = await file.text();
      setOperation("Validating CSV");
      const preview = await masterCompaniesService.importCsv(csv, true, signal);
      setValidatedCsv(csv);
      setResult(preview);
    } catch (err) {
      // An operator who closed the modal aborted the call themselves and needs no message.
      if (signal.reason === TIMED_OUT) {
        setError("Validation did not answer within two minutes. Nothing was saved; try again.");
      } else if (!signal.aborted) {
        setError(describeError(err));
      }
    } finally {
      finishRequest();
    }
  }

  async function importFile() {
    if (validatedCsv === null) return;
    setOperation("Importing companies");
    setError(null);
    const signal = startRequest();
    try {
      setResult(await masterCompaniesService.importCsv(validatedCsv, false, signal));
      setValidatedCsv(null);
      onSaved();
    } catch (err) {
      if (signal.reason === TIMED_OUT) {
        setError(
          "The import did not answer within two minutes. It may still be finishing; reload the list before retrying this file, which will skip companies that were saved.",
        );
      } else if (!signal.aborted) {
        const status = (err as { status?: number })?.status;
        const retry = status === undefined || status >= 500 || status === 408;
        setError(
          `${describeError(err)}${retry ? " If the connection was interrupted, you can retry this file safely; existing companies will be skipped." : ""}`,
        );
      }
    } finally {
      finishRequest();
    }
  }

  async function addCompany(event: FormEvent) {
    event.preventDefault();
    setOperation("Creating company");
    setError(null);
    setMessage(null);
    try {
      const added = await masterCompaniesService.create({
        name: company.name,
        website: normaliseCompanyWebsite(company.website),
        employees: MASTER_COMPANY_EMPLOYEE_BANDS.find(band => band === company.employees) ?? null,
        industrySector:
          MASTER_COMPANY_INDUSTRY_SECTORS.find(sector => sector === company.industrySector) ?? null,
      });
      setMessage(
        added.inserted
          ? "Company created."
          : "This company and website already exist. The existing record was kept.",
      );
      if (added.inserted) {
        onSaved();
        setCompany(EMPTY_COMPANY);
      }
    } catch (err) {
      setError(describeError(err));
    } finally {
      setOperation(null);
    }
  }

  if (!canWrite) return <UnauthorizedView />;

  return (
    <Modal
      isOpen
      // Closing deliberately — the header control or Escape — always works, and stops waiting on
      // an unanswered CSV call. Only the backdrop stays inert while a write is in flight, so a
      // stray click beside the modal cannot dismiss an import the operator is watching.
      onClose={() => {
        request.current?.controller.abort();
        onClose();
      }}
      title={kind === "single" ? "Create company" : "Bulk create companies"}
      // modal-overrides.css stretches "large" to 95vw, far wider than either branch needs, so
      // both size below it: the preview table wants the extra width, the stacked fields do not.
      size={kind === "single" ? "medium" : "mediumLarge"}
      backdropClick={!busy}
    >
      <div className={styles.modalContent} aria-busy={busy}>
        <p className={styles.introduction}>
          {kind === "single"
            ? "An existing company with the same name and website is kept unchanged."
            : "Matching company names and websites are skipped, leaving existing records unchanged."}
        </p>
        {operation && <ProgressNotice operation={operation} />}
        {error && (
          <div role="alert" className={`${styles.notice} ${styles.warning}`}>
            {error}
          </div>
        )}
        {message && (
          <div role="status" className={styles.notice}>
            {message}
          </div>
        )}
        {kind === "bulk" && (
          <section className={styles.section}>
            <div className={styles.importGuide}>
              <ul>
                <li>
                  UTF-8 CSV, columns <code>name,website,employees,industry_sector</code>
                </li>
                <li>Name is required; other values may be blank</li>
                <li>Use the mapped Legal 500 industry, not the Mondaq industry</li>
              </ul>
              <a
                href={`data:text/csv;charset=utf-8,${encodeURIComponent(TEMPLATE)}`}
                download="master-companies-template.csv"
              >
                Download CSV template
              </a>
            </div>
            <fieldset disabled={busy || !canWrite} className={styles.fields}>
              <label htmlFor="company-csv">
                Companies CSV (maximum 5 MB)
                <input
                  id="company-csv"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={event => {
                    setFile(event.target.files?.[0] ?? null);
                    resetPreview();
                  }}
                />
              </label>
              <Button
                variant="outlined"
                onClick={validateFile}
                disabled={!file || busy || !canWrite}
              >
                Validate and preview
              </Button>
            </fieldset>
            {result?.dryRun && (
              <>
                <p role="status">
                  Validated {result.rowsRead.toLocaleString("en-GB")} rows, nothing saved. Showing
                  the first {result.preview.length}:
                </p>
                <div className={styles.tableContainer}>
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Website</th>
                        <th>Employees</th>
                        <th>Industry</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.preview.map((row, index) => (
                        // The preview may deliberately contain duplicate name/website pairs.
                        <tr key={`${index}-${row.name}`}>
                          <td>{row.name}</td>
                          <td>{row.website || "—"}</td>
                          <td>{row.employees || "—"}</td>
                          <td>{row.industrySector || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Validation reads the file alone, so these stay advisory: polite, never an alert. */}
                {result.warnings.map(warning => (
                  <p role="status" className={`${styles.notice} ${styles.warning}`} key={warning}>
                    {warning}
                  </p>
                ))}
                {/* Rows read counts the file, not the import: the response carries no count of
                    what survives the in-file duplicate collapse, so the label promises no
                    number and the warnings above say what was dropped. */}
                <Button
                  variant="contained"
                  color="primary"
                  disabled={busy || !canWrite || validatedCsv === null}
                  onClick={importFile}
                >
                  Import companies
                </Button>
              </>
            )}
            {result && !result.dryRun && (
              <>
                <div role="status" className={styles.notice}>
                  Import complete: {result.inserted.toLocaleString("en-GB")} added,{" "}
                  {result.skipped.toLocaleString("en-GB")} duplicates skipped,{" "}
                  {result.rowsRead.toLocaleString("en-GB")} rows processed.
                </div>
                {/* Siblings of the summary, never inside it: a live region nested in another
                    live region is announced unpredictably. */}
                {result.warnings.map(warning => (
                  <p role="alert" className={`${styles.notice} ${styles.warning}`} key={warning}>
                    {warning}
                  </p>
                ))}
              </>
            )}
          </section>
        )}
        {kind === "single" && (
          <section className={styles.section}>
            <form id={COMPANY_FORM_ID} onSubmit={addCompany}>
              <fieldset disabled={busy || !canWrite} className={styles.fields}>
                {(
                  [
                    ["name", "Company name", 500],
                    ["website", "Website", 1000],
                  ] as const
                ).map(([key, label, maxLength]) => (
                  <label key={key} htmlFor={`company-${key}`}>
                    <span id={`company-${key}-label`}>
                      {label}{" "}
                      <span className={styles.optional}>
                        {key === "name" ? "(required)" : "(optional)"}
                      </span>
                    </span>
                    <input
                      id={`company-${key}`}
                      value={company[key]}
                      maxLength={maxLength}
                      required={key === "name"}
                      aria-labelledby={`company-${key}-label`}
                      aria-describedby={key === "website" ? "company-website-help" : undefined}
                      onChange={event =>
                        setCompany(current => ({ ...current, [key]: event.target.value }))
                      }
                    />
                    {key === "website" && (
                      <span id="company-website-help" className={styles.hint}>
                        We save the domain only.
                      </span>
                    )}
                  </label>
                ))}
                {(
                  [
                    ["employees", "Employees", MASTER_COMPANY_EMPLOYEE_BANDS],
                    ["industrySector", "Legal 500 industry", MASTER_COMPANY_INDUSTRY_SECTORS],
                  ] as const
                ).map(([key, label, options]) => (
                  <label key={key} htmlFor={`company-${key}`}>
                    <span>
                      {label} <span className={styles.optional}>(optional)</span>
                    </span>
                    <select
                      id={`company-${key}`}
                      value={company[key]}
                      onChange={event =>
                        setCompany(current => ({ ...current, [key]: event.target.value }))
                      }
                    >
                      <option value="" />
                      {options.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </fieldset>
            </form>
          </section>
        )}
        {kind === "single" && (
          <div className={styles.modalActions}>
            <Button
              type="submit"
              form={COMPANY_FORM_ID}
              variant="contained"
              color="primary"
              disabled={busy || !canWrite || !company.name.trim()}
            >
              Create company
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
